import { AxiosRequestConfig, AxiosResponse } from 'axios'

import apiClient, { publicApiClient } from '@core/api/client'
import { CreateLocationRequestDTO } from '@core/api/dtos/request/location/create'
import { ShareLocationRequestDTO } from '@core/api/dtos/request/location/share'
import { UpdateLocationRequestDTO } from '@core/api/dtos/request/location/update'
import { UpdateLocationZoneVolumeRequestDTO } from '@core/api/dtos/request/location/updateZoneVolume'

import { ContactUpdateRequestDTO } from '../dtos/request/location/contactUpdate'

export const locationService = {
	/**
	 * Paginate account locations
	 */
	paginateLocations(config?: AxiosRequestConfig): Promise<AxiosResponse> {
		return apiClient.get('/my/locations', config)
	},

	/**
	 * Get a location by id (authenticated)
	 */
	getLocation(
		locationId: number,
		config?: AxiosRequestConfig
	): Promise<AxiosResponse> {
		return apiClient.get(`/my/locations/${locationId}`, config)
	},

	/**
	 * Get a location by UUID (public/shared page — no auth required).
	 * Default include ensures zones have box_allocations for getActiveBoxAllocation().
	 */
	getLocationByUUID(
		uuid: string,
		config?: AxiosRequestConfig
	): Promise<AxiosResponse> {
		return publicApiClient.get(`/locations/${uuid}`, {
			...config,
			params: {
				...config?.params,
				include:
					config?.params?.include ??
					'players.state,players.box_allocations,lastGooglePlaceData,account',
			},
		})
	},

	/**
	 * Create a location
	 */
	createLocation(
		data: CreateLocationRequestDTO,
		config?: AxiosRequestConfig
	): Promise<AxiosResponse> {
		return apiClient.post('/my/locations', data, config)
	},

	/**
	 * Update a location
	 */
	patchLocation(
		locationId: number,
		data: UpdateLocationRequestDTO,
		config?: AxiosRequestConfig
	): Promise<AxiosResponse> {
		return apiClient.patch(`/my/locations/${locationId}`, data, config)
	},

	/**
	 * Update a location
	 */
	patchContactLocation(
		locationId: number,
		data: ContactUpdateRequestDTO,
		config?: AxiosRequestConfig
	): Promise<AxiosResponse> {
		return apiClient.patch(`/my/locations/${locationId}`, data, config)
	},

	/**
	 * Delete a location
	 */
	deleteLocation(
		locationId: number,
		config?: AxiosRequestConfig
	): Promise<AxiosResponse> {
		return apiClient.delete(`/my/locations/${locationId}`, config)
	},

	/**
	 * Share a location link (public — no auth required)
	 */
	shareLocationLink(
		locationId: number,
		data: ShareLocationRequestDTO,
		config?: AxiosRequestConfig
	): Promise<AxiosResponse> {
		return apiClient.post(`/my/locations/${locationId}/share`, data, config)
	},

	/**
	 * Update a zone's volume settings from a shared/public location page (no auth required)
	 */
	patchLocationZoneVolume(
		locationUUID: string,
		zoneId: number,
		data: UpdateLocationZoneVolumeRequestDTO,
		config?: AxiosRequestConfig
	): Promise<AxiosResponse> {
		return publicApiClient.patch(
			`/locations/${locationUUID}/zones/${zoneId}`,
			data,
			config
		)
	},
}
